import { useRef, useState } from "react"

import LocationField from "shared/components/stored_fields/location_field/index"

import type { LocationPickerData, LocationPickerValue, UIEvent } from "../types"
import { CardActions, CardButton, CardShell } from "./card_shell"
import styles from "./location_picker_card.module.scss"

interface Props {
  ui: UIEvent
  onRespond: (uiId: string, actionId: string, value: unknown, displayText?: string) => void
}

type FieldLocation = {
  latitude: number
  longitude: number
  radius?: number
  formatted_address?: string
}

const DEFAULT_RADIUS_METERS = 1000
const FALLBACK_CENTER = { latitude: 37.78578938794374, longitude: -122.41903230000003 }

const LocationPickerCard = ({ ui, onRespond }: Props) => {
  const [answered, setAnswered] = useState(false)

  const { prompt, subtype, default: initialDefault } = ui.data as LocationPickerData
  const isPoint = subtype === "location_point"

  const center = initialDefault ?? FALLBACK_CENTER
  const initialRadius = initialDefault?.radius_in_meters ?? DEFAULT_RADIUS_METERS

  const latest = useRef<FieldLocation>({
    latitude: center.latitude,
    longitude: center.longitude,
    radius: initialRadius,
  })

  const initialLocation = {
    lat: center.latitude,
    lng: center.longitude,
    radius: initialRadius,
    is_default_location: !initialDefault,
  }

  const handleChange = (location: FieldLocation) => {
    latest.current = {
      latitude: location.latitude,
      longitude: location.longitude,
      radius: location.radius,
      formatted_address: location.formatted_address,
    }
  }

  const submit = () => {
    if (answered) return
    setAnswered(true)
    const { latitude, longitude, radius, formatted_address } = latest.current
    const value: LocationPickerValue = isPoint
      ? { type: "location_point", latitude, longitude }
      : { type: "location_radius", latitude, longitude, radius_in_meters: Math.round(radius ?? DEFAULT_RADIUS_METERS) }
    onRespond(ui.id, "select", value, formatted_address || `${latitude}, ${longitude}`)
  }

  return (
    <CardShell prompt={prompt} onDismiss={() => onRespond(ui.id, "close", undefined)}>
      <div className={styles["map"]}>
        <LocationField
          location={initialLocation}
          fieldName="chat_location"
          mapDirection={subtype}
          onChange={handleChange}
          disabled={answered}
        />
      </div>
      <CardActions>
        <CardButton variant="primary" disabled={answered} onClick={submit}>
          Select
        </CardButton>
      </CardActions>
    </CardShell>
  )
}

export default LocationPickerCard
